import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import LocationSelectorForForm from '../locaties/LocationSelectorForForm.js';
import './MeetingRounds.css';

function NewMeetingRound() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();

  const userRole = userInfo?.role || '';
  const isSingleUser = Boolean(userInfo?.is_single_user);

  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [docenten, setDocenten] = useState([]);
  const [docentSearch, setDocentSearch] = useState('');
  const [selectedDates, setSelectedDates] = useState(['']);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [isResolvingParticipants, setIsResolvingParticipants] = useState(false);
  const [participantLoadError, setParticipantLoadError] = useState('');

  const canManageRounds =
    userRole === 'super_admin' ||
    userRole === 'locatie_admin' ||
    userRole === 'docent';

  const getCurrentSchoolYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const startYear = month >= 8 ? year : year - 1;
    return `${startYear}-${startYear + 1}`;
  };

  const normalizeSchoolYear = (schoolYear) =>
    String(schoolYear || '')
      .trim()
      .replace('/', '-');

  const getGroupSchoolYear = (group) =>
    group.school_year ||
    group.schoolYear ||
    group.schooljaar ||
    group.academic_year ||
    '';

  const getGroupLocationId = (group) =>
    group.location_id ||
    group.locatie_id ||
    group.location?.location_id ||
    group.location?.id ||
    '';

  const getAutomaticLocationId = (groupIds = []) => {
    const selectedLocationIds = [
      ...new Set(
        groups
          .filter((group) => groupIds.includes(String(group.groep_id)))
          .map(getGroupLocationId)
          .filter(Boolean)
          .map(String)
      )
    ];

    if (selectedLocationIds.length === 1) {
      return selectedLocationIds[0];
    }

    return String(
      userInfo?.location_id ||
      userInfo?.locatie_id ||
      userInfo?.location?.location_id ||
      userInfo?.location?.id ||
      ''
    );
  };

  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getDefaultBookingOpenAt = () => {
    const now = new Date();
    now.setHours(9, 0, 0, 0);
    return formatDateTimeLocal(now);
  };

  const getDefaultBookingCloseAt = () => {
    const now = new Date();
    const endOfYear = new Date(now.getFullYear(), 11, 31, 9, 0, 0, 0);
    return formatDateTimeLocal(endOfYear);
  };

  const schoolYearOptions = useMemo(() => {
    const current = getCurrentSchoolYear();
    const [start] = current.split('-').map(Number);

    return [
      `${start - 1}-${start}`,
      current,
      `${start + 1}-${start + 2}`,
      `${start + 2}-${start + 3}`
    ];
  }, []);

  const [values, setValues] = useState({
    title: '',
    description: '',
    school_year: getCurrentSchoolYear(),
    booking_open_at: getDefaultBookingOpenAt(),
    booking_close_at: getDefaultBookingCloseAt(),
    location_id: '',
    group_ids: [],
    docent_ids: [],
    selected_participants: []
  });

  useEffect(() => {
    if (userRole === 'super_admin' || userRole === 'locatie_admin') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/alllocations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => {
          console.error('Error fetching locations', err);
          setLocations([]);
        });
    }
  }, [userRole, token]);

  useEffect(() => {
    if (!canManageRounds) return;

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/group/all`, {
        params: {
          school_year: getCurrentSchoolYear(),
          ...(!isSingleUser && values.location_id
            ? { location_id: values.location_id }
            : {})
        },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        const data = res.data.data || res.data.Data || res.data;
        const groupList = Array.isArray(data) ? data : [];
        const responseContainsSchoolYears = groupList.some((group) =>
          Boolean(getGroupSchoolYear(group))
        );

        setGroups(
          responseContainsSchoolYears
            ? groupList.filter(
                (group) =>
                  normalizeSchoolYear(getGroupSchoolYear(group)) ===
                  normalizeSchoolYear(getCurrentSchoolYear())
              )
            : groupList
        );
      })
      .catch((err) => {
        console.error('Error fetching groups', err);
        setGroups([]);
      });
  }, [token, values.location_id, isSingleUser, canManageRounds]);

  useEffect(() => {
    if (!canManageRounds) return;

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/docent/alldocent`, {
        params: !isSingleUser && values.location_id ? { location_id: values.location_id } : {},
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        const data = res.data.data || res.data.Data || res.data;
        setDocenten(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching docenten', err);
        setDocenten([]);
      });
  }, [token, values.location_id, isSingleUser, canManageRounds]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const selectedGroups = useMemo(
    () => groups.filter((group) => values.group_ids.includes(group.groep_id)),
    [groups, values.group_ids]
  );

  const availableGroups = useMemo(() => {
    const term = groupSearch.trim().toLowerCase();

    return groups.filter((group) => {
      const alreadySelected = values.group_ids.includes(group.groep_id);
      if (alreadySelected) return false;

      const haystack = `${group.groep_name || ''} ${group.location_name || ''}`.toLowerCase();
      return term === '' || haystack.includes(term);
    });
  }, [groups, values.group_ids, groupSearch]);

  const getDocentName = (docent) => {
    const fullName = `${docent.firstname || ''} ${docent.lastname || ''}`.trim();
    return fullName || docent.name || docent.email || docent.docent_id;
  };

  const selectedDocenten = useMemo(
    () => docenten.filter((docent) => values.docent_ids.includes(docent.docent_id)),
    [docenten, values.docent_ids]
  );

  const availableDocenten = useMemo(() => {
    const term = docentSearch.trim().toLowerCase();

    return docenten.filter((docent) => {
      const alreadySelected = values.docent_ids.includes(docent.docent_id);
      if (alreadySelected) return false;

      const haystack = `${getDocentName(docent)} ${docent.email || ''} ${docent.location_name || ''}`.toLowerCase();
      return term === '' || haystack.includes(term);
    });
  }, [docenten, values.docent_ids, docentSearch]);

  const selectedParticipantKeys = new Set(
    (values.selected_participants || []).map(
      (item) => `${item.student_id}-${item.parent_id}`
    )
  );

  const availableParticipants = participants.filter((item) => {
    const key = `${item.student_id}-${item.parent_id}`;
    if (selectedParticipantKeys.has(key)) return false;

    const term = participantSearch.trim().toLowerCase();
    if (!term) return true;

    const haystack =
      `${item.student_name || ''} ${item.parent_name || ''} ${item.parent_email || ''}`.toLowerCase();

    return haystack.includes(term);
  });

  const selectedParticipantsDetailed = participants.filter((item) =>
    selectedParticipantKeys.has(`${item.student_id}-${item.parent_id}`)
  );

  const handleRemoveParticipant = (studentId, parentId) => {
    setValues((prev) => ({
      ...prev,
      selected_participants: (prev.selected_participants || []).filter(
        (item) => !(item.student_id === studentId && item.parent_id === parentId)
      )
    }));
  };

  const handleAddParticipant = (studentId, parentId) => {
    const exists = (values.selected_participants || []).some(
      (item) => item.student_id === studentId && item.parent_id === parentId
    );

    if (exists) return;

    setValues((prev) => ({
      ...prev,
      selected_participants: [
        ...(prev.selected_participants || []),
        { student_id: studentId, parent_id: parentId }
      ]
    }));
  };

  const handleGroupSelect = async (e) => {
    const groepId = e.target.value;
    if (!groepId) return;
    await handleAddGroup(groepId);
    e.target.value = '';
  };

  const handleDocentSelect = (e) => {
    const docentId = e.target.value;
    if (!docentId) return;

    setValues((prev) => ({
      ...prev,
      docent_ids: prev.docent_ids.includes(docentId)
        ? prev.docent_ids
        : [...prev.docent_ids, docentId]
    }));

    e.target.value = '';
  };

  const handleRemoveDocent = (docentId) => {
    setValues((prev) => ({
      ...prev,
      docent_ids: prev.docent_ids.filter((id) => id !== docentId)
    }));
  };

  const handleParticipantSelect = (e) => {
    const value = e.target.value;
    if (!value) return;

    const [studentId, parentId] = value.split('|');
    handleAddParticipant(studentId, parentId);
    e.target.value = '';
  };

  const handleAddGroup = async (groepId) => {
    const nextGroupIds = [...values.group_ids, groepId];
    const automaticLocationId =
      values.location_id || getAutomaticLocationId(nextGroupIds);

    setValues((prev) => ({
      ...prev,
      group_ids: nextGroupIds,
      location_id: automaticLocationId
    }));

    await resolveParticipants(nextGroupIds, automaticLocationId);
  };

  const handleRemoveGroup = async (groepId) => {
    const nextGroupIds = values.group_ids.filter((id) => id !== groepId);
    const automaticLocationId =
      values.location_id || getAutomaticLocationId(nextGroupIds);

    setValues((prev) => ({
      ...prev,
      group_ids: nextGroupIds
    }));

    await resolveParticipants(nextGroupIds, automaticLocationId);
  };

  const resolveParticipants = useCallback(async (nextGroupIds, nextLocationId, keepExisting = true) => {
    if (!nextGroupIds || nextGroupIds.length === 0) {
      setParticipants([]);
      setValues((prev) => ({
        ...prev,
        selected_participants: []
      }));
      return;
    }

    setIsResolvingParticipants(true);
    setParticipantLoadError('');

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/meeting-round/resolve-participants`,
        {
          group_ids: nextGroupIds,
          location_id: nextLocationId || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const suggested = res.data?.data?.suggested_participants || [];
      const normalizedSuggested = (Array.isArray(suggested) ? suggested : []).map(
        (item) => ({
          ...item,
          parent_email:
            item.parent_email ||
            item.parent_user_email ||
            item.user_email ||
            item.email ||
            ''
        })
      );

      setParticipants(normalizedSuggested);

      setValues((prev) => {
        const suggestedKeys = new Set(
          normalizedSuggested.map((item) => `${item.student_id}-${item.parent_id}`)
        );

        const autoSelected = normalizedSuggested.map((item) => ({
          student_id: item.student_id,
          parent_id: item.parent_id
        }));

        if (!keepExisting) {
          return {
            ...prev,
            selected_participants: autoSelected
          };
        }

        const existingManual = (prev.selected_participants || []).filter(
          (item) => !suggestedKeys.has(`${item.student_id}-${item.parent_id}`)
        );

        return {
          ...prev,
          selected_participants: [...autoSelected, ...existingManual]
        };
      });
    } catch (err) {
      console.error('Error resolving participants', err);
      setParticipantLoadError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'De deelnemers konden niet worden geladen.'
      );
      setParticipants([]);
      setValues((prev) => ({
        ...prev,
        selected_participants: []
      }));
    } finally {
      setIsResolvingParticipants(false);
    }
  }, [token]);

  const handleAddDate = () => {
    setSelectedDates((prev) => [...prev, '']);
  };

  const handleRemoveDate = (index) => {
    const dateValue = selectedDates[index];

    setSelectedDates((prev) => prev.filter((_, i) => i !== index));

    if (dateValue) {
      setSlotsByDate((prev) => {
        const updated = { ...prev };
        delete updated[dateValue];
        return updated;
      });
    }
  };

  const handleDateChange = (index, newDate) => {
    setSelectedDates((prev) => {
      const updated = [...prev];
      const oldDate = updated[index];
      updated[index] = newDate;

      if (oldDate && oldDate !== newDate) {
        setSlotsByDate((current) => {
          const next = { ...current };
          const existingSlots = next[oldDate] || [];
          delete next[oldDate];

          if (newDate) {
            next[newDate] = existingSlots;
          }

          return next;
        });
      } else if (newDate) {
        setSlotsByDate((current) => ({
          ...current,
          [newDate]: current[newDate] || []
        }));
      }

      return updated;
    });
  };

  const addSlotRow = (date) => {
    if (!date) return;

    setSlotsByDate((prev) => ({
      ...prev,
      [date]: [
        ...(prev[date] || []),
        {
          start_time: '',
          end_time: '',
          note: ''
        }
      ]
    }));
  };

  const removeSlotRow = (date, index) => {
    setSlotsByDate((prev) => ({
      ...prev,
      [date]: (prev[date] || []).filter((_, i) => i !== index)
    }));
  };

  const updateSlotRow = (date, index, field, value) => {
    setSlotsByDate((prev) => ({
      ...prev,
      [date]: (prev[date] || []).map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const validateForm = () => {
    if (!values.title.trim()) {
      alert('Titel is verplicht.');
      return false;
    }

    if (!values.school_year.trim()) {
      alert('Schooljaar is verplicht.');
      return false;
    }

    if (!values.booking_open_at || !values.booking_close_at) {
      alert('Vul boeking open en boeking sluit in.');
      return false;
    }

    if (new Date(values.booking_close_at) <= new Date(values.booking_open_at)) {
      alert('Boeking sluit moet later zijn dan boeking open.');
      return false;
    }

    if (!isSingleUser && !values.location_id && (userRole === 'super_admin' || userRole === 'locatie_admin')) {
      alert('Selecteer een locatie.');
      return false;
    }

    if ((userRole === 'super_admin' || userRole === 'locatie_admin') && (values.docent_ids || []).length === 0) {
      alert('Selecteer minimaal één docent.');
      return false;
    }

    if ((values.selected_participants || []).length === 0) {
      alert('Selecteer minimaal één ouder/deelnemer.');
      return false;
    }

    const validDates = selectedDates.filter(Boolean);

    if (validDates.length === 0) {
      alert('Voeg minimaal één datum toe.');
      return false;
    }

    for (const date of validDates) {
      const slots = slotsByDate[date] || [];

      if (slots.length === 0) {
        alert(`Voeg minimaal één tijdslot toe voor ${date}.`);
        return false;
      }

      for (const slot of slots) {
        if (!slot.start_time || !slot.end_time) {
          alert(`Vul starttijd en eindtijd in voor ${date}.`);
          return false;
        }

        if (slot.end_time <= slot.start_time) {
          alert(`Eindtijd moet later zijn dan starttijd voor ${date}.`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        ...values,
        status: 'draft',
        location_id:
          values.location_id ||
          getAutomaticLocationId(values.group_ids) ||
          null,
        slots_by_date: Object.fromEntries(
          Object.entries(slotsByDate).filter(([date, slots]) => date && slots.length > 0)
        )
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/meeting-round/add`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      navigate('/meeting-rounds');
    } catch (error) {
      console.error('Error creating meeting round', error);
      alert(error?.response?.data?.error || error?.response?.data?.message || 'Opslaan mislukt.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/meeting-rounds');
  };

  if (!canManageRounds) {
    return (
      <div className="bg-white p-4">
        <h2 className="text-center">Geen toegang</h2>
        <p className="text-center text-muted">
          Je hebt geen rechten om een oudergesprek-ronde aan te maken.
        </p>
        <div className="row mt-4">
          <div className="col-md-6 mx-auto">
            <button
              type="button"
              className="btn btn-secondary w-100 rounded-0"
              onClick={handleCancel}
            >
              Terug
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white p-4">
        <div>
          <h2 className="text-center">Nieuwe oudergesprek-ronde</h2>
        </div>

        <hr className="seperator" />

        <form onSubmit={handleSubmit} className="form-student">
          {!isSingleUser && (userRole === 'super_admin' || userRole === 'locatie_admin') && (
            <div className="row">
              <LocationSelectorForForm
                userRole={userRole}
                value={values.location_id ?? ''}
                onChange={async (e) => {
                  const nextLocationId = e.target.value || '';

                  setValues((prev) => ({
                    ...prev,
                    location_id: nextLocationId,
                    group_ids: [],
                    docent_ids: [],
                    selected_participants: []
                  }));

                  setParticipants([]);
                  setParticipantLoadError('');
                }}
                locations={locations}
              />
            </div>
          )}

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="title">
                Titel<span style={{ color: 'red' }}>*</span>
              </label>
              <input
                className="form-control rounded-0"
                type="text"
                name="title"
                placeholder="Bijv. Oudergesprekken voorjaar groep 7"
                required
                value={values.title}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3 col">
              <label htmlFor="school_year">
                Schooljaar<span style={{ color: 'red' }}>*</span>
              </label>
              <select
                className="form-select rounded-0"
                name="school_year"
                value={values.school_year}
                onChange={handleChange}
                required
              >
                {schoolYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="description">Omschrijving</label>
            <textarea
              className="form-control rounded-0"
              rows="4"
              name="description"
              placeholder="Korte toelichting aan de ouders."
              value={values.description}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label>Zoek en selecteer groepen</label>
            <input
              type="text"
              className="form-control rounded-0 mb-2"
              placeholder="Zoek groep op naam of locatie"
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
            />

            <select
              className="form-select rounded-0 meeting-choice-select"
              value=""
              onChange={handleGroupSelect}
            >
              <option value="">-- Kies een groep --</option>
              {availableGroups.map((group) => (
                <option key={group.groep_id} value={group.groep_id}>
                  {group.groep_name}{group.location_name ? ` — ${group.location_name}` : ''}
                </option>
              ))}
            </select>

            <div className="mt-3">
              <label className="mb-2">Geselecteerde groepen</label>
              <div className="meeting-selected-list">
                {selectedGroups.length > 0 ? (
                  selectedGroups.map((group) => (
                    <div key={group.groep_id} className="meeting-selected-row">
                      <span>
                        {group.groep_name}
                        {group.location_name ? ` — ${group.location_name}` : ''}
                      </span>
                      <button
                        type="button"
                        className="meeting-remove-btn"
                        onClick={() => handleRemoveGroup(group.groep_id)}
                        aria-label="Groep verwijderen"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-muted small">Nog geen groepen geselecteerd.</div>
                )}
              </div>
            </div>
          </div>

          <hr className="seperator" />
          <p className="text-center">Docenten</p>

          <div className="mb-3">
            <label>Zoek en selecteer docenten</label>
            <input
              type="text"
              className="form-control rounded-0 mb-2"
              placeholder="Zoek docent op naam, e-mail of locatie"
              value={docentSearch}
              onChange={(e) => setDocentSearch(e.target.value)}
            />

            <select
              className="form-select rounded-0 meeting-choice-select"
              value=""
              onChange={handleDocentSelect}
            >
              <option value="">-- Kies een docent --</option>
              {availableDocenten.map((docent) => (
                <option key={docent.docent_id} value={docent.docent_id}>
                  {getDocentName(docent)}{docent.location_name ? ` — ${docent.location_name}` : ''}{docent.email ? ` (${docent.email})` : ''}
                </option>
              ))}
            </select>

            <div className="mt-3">
              <label className="mb-2">Gekoppelde docenten</label>
              <div className="meeting-selected-list">
                {selectedDocenten.length > 0 ? (
                  selectedDocenten.map((docent) => (
                    <div key={docent.docent_id} className="meeting-selected-row">
                      <span>
                        {getDocentName(docent)}
                        {docent.email ? ` (${docent.email})` : ''}
                      </span>
                      <button
                        type="button"
                        className="meeting-remove-btn"
                        onClick={() => handleRemoveDocent(docent.docent_id)}
                        aria-label="Docent verwijderen"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-muted small">Nog geen docenten geselecteerd.</div>
                )}
              </div>
            </div>
          </div>

          <hr className="seperator" />
          <p className="text-center">Deelnemers</p>

          <div className="mb-3">
            <label>Zoek en selecteer deelnemers</label>

            {isResolvingParticipants ? (
              <p className="text-muted">Deelnemers laden...</p>
            ) : (
              <>
                {participantLoadError && (
                  <div className="alert alert-danger rounded-0" role="alert">
                    {participantLoadError}
                  </div>
                )}

                <input
                  type="text"
                  className="form-control rounded-0 mb-2"
                  placeholder="Zoek op leerling, ouder of e-mail"
                  value={participantSearch}
                  onChange={(e) => setParticipantSearch(e.target.value)}
                />

                <select
                  className="form-select rounded-0 meeting-choice-select"
                  value=""
                  onChange={handleParticipantSelect}
                >
                  <option value="">-- Kies een leerling/ouder --</option>
                  {availableParticipants.map((item) => (
                    <option
                      key={`${item.student_id}-${item.parent_id}`}
                      value={`${item.student_id}|${item.parent_id}`}
                    >
                      {item.student_name} — {item.parent_name}{item.parent_email ? ` (${item.parent_email})` : ''}
                    </option>
                  ))}
                </select>

                <div className="mt-3">
                  <label className="mb-2">Geselecteerde deelnemers</label>
                  <div className="meeting-selected-list">
                    {selectedParticipantsDetailed.length > 0 ? (
                      selectedParticipantsDetailed.map((item) => (
                        <div
                          key={`${item.student_id}-${item.parent_id}`}
                          className="meeting-selected-row"
                        >
                          <span>
                            {item.student_name} — {item.parent_name}
                            {item.parent_email
                              ? ` (${item.parent_email})`
                              : ' (geen e-mailadres geregistreerd)'}
                          </span>
                          <button
                            type="button"
                            className="meeting-remove-btn"
                            onClick={() =>
                              handleRemoveParticipant(item.student_id, item.parent_id)
                            }
                            aria-label="Deelnemer verwijderen"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted small">
                        Nog geen deelnemers geselecteerd.
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <hr className="seperator" />
          <p className="text-center">Boekingsperiode</p>

          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="booking_open_at">
                Boeking open vanaf<span style={{ color: 'red' }}>*</span>
              </label>
              <input
                className="form-control rounded-0"
                type="datetime-local"
                name="booking_open_at"
                value={values.booking_open_at}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3 col">
              <label htmlFor="booking_close_at">
                Boeking sluit op<span style={{ color: 'red' }}>*</span>
              </label>
              <input
                className="form-control rounded-0"
                type="datetime-local"
                name="booking_close_at"
                value={values.booking_close_at}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <hr className="seperator" />
          <p className="text-center">Slots per datum</p>

          {selectedDates.map((dateValue, dateIndex) => (
            <div key={`date-${dateIndex}`} className="meeting-date-block">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div className="meeting-date-title-wrap">
                  <label className="mb-1 fw-semibold">Datum</label>
                  <input
                    type="date"
                    className="form-control rounded-0"
                    value={dateValue}
                    onChange={(e) => handleDateChange(dateIndex, e.target.value)}
                  />
                </div>

                {selectedDates.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleRemoveDate(dateIndex)}
                  >
                    Datum verwijderen
                  </button>
                )}
              </div>

              {dateValue && (
                <>
                  <div className="meeting-slot-header d-none d-md-grid">
                    <span>Starttijd</span>
                    <span>Eindtijd</span>
                    <span>Opmerking</span>
                    <span></span>
                  </div>

                  {(slotsByDate[dateValue] || []).map((slot, slotIndex) => (
                    <div key={`${dateValue}-${slotIndex}`} className="meeting-slot-row">
                      <div>
                        <label className="d-md-none small fw-semibold">Starttijd</label>
                        <input
                          type="time"
                          className="form-control rounded-0"
                          value={slot.start_time}
                          onChange={(e) =>
                            updateSlotRow(dateValue, slotIndex, 'start_time', e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <label className="d-md-none small fw-semibold">Eindtijd</label>
                        <input
                          type="time"
                          className="form-control rounded-0"
                          value={slot.end_time}
                          onChange={(e) =>
                            updateSlotRow(dateValue, slotIndex, 'end_time', e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <label className="d-md-none small fw-semibold">Opmerking</label>
                        <input
                          type="text"
                          className="form-control rounded-0"
                          value={slot.note}
                          placeholder="Optioneel"
                          onChange={(e) =>
                            updateSlotRow(dateValue, slotIndex, 'note', e.target.value)
                          }
                        />
                      </div>

                      <div className="meeting-slot-delete-wrap">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeSlotRow(dateValue, slotIndex)}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => addSlotRow(dateValue)}
                    >
                      Tijd toevoegen
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          <div className="mb-4">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAddDate}
            >
              Datum toevoegen
            </button>
          </div>

          <div className="row">
            <div className="col-md-6">
              <button
                type="button"
                className="btn btn-secondary w-100 rounded-0"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Annuleren
              </button>
            </div>

            <div className="col-md-6">
              <button
                type="submit"
                className="btn btn-secondary w-100 rounded-0"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="spacer-bottom"></div>
    </div>
  );
}

export default NewMeetingRound;
